import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Routes} from "../../../routes";
import {RootState} from "../../../store";
import PersonaSignUp from "../../../components/personalSignup"
import BusinessSignup from "../../../components/businessSignup"
import {
  FormCard,
  FormCardHeader,
  Line,
  LinkContainer,
  SectionColumn,
  StyledLink,
  StyledA
} from 'styles/styled';
import SvgBusinessIcon from "../../../assets/svg/business-account-icon";
import SvgPersonalIcon from "../../../assets/svg/personal-account-icon";
import styled from "styled-components";
import {AuthActions} from "../../../store/auth";
import { Breakpoints, Colors, Spacing } from "styles/constants";
import {AnimatePresence} from "framer-motion";
import { Checkbox } from "shared-react";
import MotionDiv from "../../../components/helperComponents/motionDiv";


const SignupOptionContainer = styled.div<{active: boolean}>`{
  width: 100%;
  margin: auto auto ${Spacing.Space3} auto;
  position: relative;
  cursor: pointer;
  border-radius: 6px;
  padding: 24px 20px;
  display: flex;
  box-sizing: border-box;
  align-items: center;
  justify-content: space-between;
  ${props => props.active ? `
    border: 1px solid ${Colors.Action25};
    background: ${Colors.Action25};
  ` : `
    border: 1px solid ${Colors.Air};
    margin: auto auto ${Spacing.Space3} auto;
  `}
}`;

const IconBox = styled.div<{active: boolean}>`
  height: 60px;
  width: 60px;
  border-radius: 12px;
  display: flex;
  justify-self: end;
  justify-content: center;
  align-items: center;

  @media (max-width: ${Breakpoints.Mobile}) {
    display: none
  }
  
  ${props => props.active ? `
    background: ${Colors.White};
    border: 1px solid ${Colors.White};
  ` : `
    background: ${Colors.Action25};
    border: 1px solid ${Colors.Action25};
  `}
`;

const PositionLeft = styled.div`{
  display: flex;
  flex-direction: row;
  align-items: center;
}`;

const OptionCopy = styled.div `{
   margin-left: ${Spacing.Space2};
   font-size: 12px;
   font-weight: 400;
   line-height: 18px;
   list-style-position: inside;
 }`;

const OptionTitle = styled.span `{
  line-height: 18px;
  font-weight: 500;
  font-size: 16px;
}`;

const List = styled.ul `
  list-style: none;
  
  li {
    &:before {
      content: "\\2022"; /* Add content: \\2022 is the CSS Code/unicode for a bullet */
      color: ${Colors.Go}; /* Change the color */
      font-weight: 900; /* If you want it to be bold */
      font-size: 15px;
      display: inline-block; /* Needed to add space between the bullet and the text */
      width: 1em; /* Also needed for space (tweak if needed) */
    }
  }
`;

const FeeContainer = styled(LinkContainer) `{
  margin-bottom: ${Spacing.Space4};
}`

const FormCardHeaderExtended = styled(FormCardHeader)`
  margin-bottom: ${Spacing.Space2};
`;

const FormCardH2 = styled(FormCardHeader)`
  font-size: 18px;
  line-height: 25px;
  margin-bottom: ${Spacing.Space3};
`;

const FormCardHeaderSubtext = styled(FormCardHeader)`
  color: ${Colors.SlateDark};
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
`;

const SelectAccountType = () => {

  const dispatch = useDispatch();
  const urlExtension = useSelector((state: RootState) => state.app.urlExtension);
  const [signupType, setSignupType] = useState('');

  const handleOptionClick = (option: 'personal' | 'business') => {
    dispatch(AuthActions.clearError(0));
    setSignupType(option);
  }

  useEffect(() => {
    const initialSignupType = urlExtension.includes('business') ? 'business' : 'personal';
    setSignupType(initialSignupType);
  }, [urlExtension])

  return (
    <>
      <FormCard>
        <FormCardHeaderExtended>Welcome to BitPay</FormCardHeaderExtended>
        <FormCardHeaderSubtext>BitPay provides an easy way to pay with and get paid in crypto. Create a business or personal account below to get started.</FormCardHeaderSubtext>
        <Line/>
        <FormCardH2>How would you like to use BitPay?</FormCardH2>
        <SectionColumn style={{marginBottom: '0px'}}>
          <SignupOptionContainer role="button" aria-label="select personal" active={signupType === 'personal'} onClick={() => handleOptionClick('personal')}>
            <PositionLeft>
              <div>
                <Checkbox checked={signupType === 'personal'} useOnChange={false}/>
              </div>
              <OptionCopy>
                <OptionTitle>Personal Use</OptionTitle>
                <List>
                  <li><span>Pay with or get paid in crypto.</span></li>
                  <li><span>Apply for the BitPay debit card.</span></li>
                </List>
              </OptionCopy>
            </PositionLeft>
            <div>
              <IconBox active={signupType === 'personal'}>
                <SvgPersonalIcon/>
              </IconBox>
            </div>
          </SignupOptionContainer>
          <SignupOptionContainer role="button" aria-label="select business" active={signupType === 'business'} onClick={() => handleOptionClick('business')}>
            <PositionLeft>
              <div>
                <Checkbox checked={signupType === 'business'} useOnChange={false}/>
              </div>
              <OptionCopy>
                <OptionTitle>Business Use</OptionTitle>
                <List>
                  <li><span>Accept crypto payments.</span></li>
                  <li><span>Pay out crypto to anyone, anywhere.</span></li>
                </List>
              </OptionCopy>
            </PositionLeft>
            <div>
              <IconBox active={signupType === 'business'}>
                <SvgBusinessIcon/>
              </IconBox>
            </div>
          </SignupOptionContainer>
        </SectionColumn>
        <FeeContainer>
          {signupType === 'business' && <strong>An application fee is required.</strong>}
          <p>Not ready to create an account? <StyledA href='/contact-sales/'>Contact Sales</StyledA></p>
        </FeeContainer>
        <Line/>
        <AnimatePresence exitBeforeEnter>
          {signupType === 'personal' &&
            <MotionDiv
              key="personal"
            >
              <PersonaSignUp/>
            </MotionDiv>}
          {signupType === 'business' && <MotionDiv
              key="business">
              <BusinessSignup/>
          </MotionDiv>
          }
        </AnimatePresence>
        </FormCard>
      <LinkContainer>
        <p>Already have an account? <StyledLink role="link" to={Routes.LOGIN + urlExtension}>Login</StyledLink></p>
      </LinkContainer>
    </>
  )
};

export default SelectAccountType;
